// src/pages/Reports.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../api";
import Books from "./Books";
import { ui } from "../uiStyles";
import HeadquartersPage from "../components/headquarters/HeadquartersPage";
import HeadquartersSection from "../components/headquarters/HeadquartersSection";
import HeadquartersMetricCard from "../components/headquarters/HeadquartersMetricCard";

function money(n) {
  const x = Number(n || 0);
  return x.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function pct(n) {
  return `${Math.round((Number(n || 0)) * 100)}%`;
}

function daysPastDue(dueDateStr) {
  if (!dueDateStr) return null;
  const due = new Date(dueDateStr);
  if (Number.isNaN(due.getTime())) return null;

  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const startOfDue = new Date(
    due.getFullYear(),
    due.getMonth(),
    due.getDate()
  );

  const diffDays = Math.floor(
    (startOfToday - startOfDue) / (24 * 60 * 60 * 1000)
  );
  return diffDays > 0 ? diffDays : 0;
}

export default function Reports() {
  const navigate = useNavigate();

  const [module, setModule] = useState("job_profitability");
  const [range, setRange] = useState("30");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [ar, setAr] = useState(null);
  const [top, setTop] = useState(null);
  const [paid, setPaid] = useState(null);
  const [profitability, setProfitability] = useState(null);

  const days = useMemo(() => parseInt(range, 10) || 30, [range]);
  const rangeLabel =
    days === 30 ? "Last 30 days" : days === 90 ? "Last 90 days" : `Last ${days} days`;

  function exportProfitabilityCsv() {
    const rows = profitability?.rows || [];
    const header = [
      "Job",
      "Client",
      "Revenue",
      "Costs",
      "Profit",
      "Margin",
      "Status",
    ];

    const csvRows = [
      header.join(","),
      ...rows.map((r) =>
        [
          r.job_number || `JOB #${r.job_id}`,
          r.client_name || "",
          r.revenue || 0,
          r.costs || 0,
          r.profit || 0,
          `${Number(r.margin || 0).toFixed(2)}%`,
          r.status || "",
        ]
          .map((v) => `"${String(v).replaceAll('"', '""')}"`)
          .join(",")
      ),
    ];

    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `job-profitability-${days}-days.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function loadAR() {
    const res = await fetch(`/api/reports/ar-overview?days=${days}`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to load AR overview (HTTP ${res.status})`);
    return res.json();
  }

  async function loadTopClients() {
    const res = await fetch(`/api/reports/top-clients-ar?days=${days}&limit=10`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to load Top Clients AR (HTTP ${res.status})`);
    return res.json();
  }

  async function loadJobProfitability() {
    return apiRequest(`/reports/job-profitability?days=${days}`);
  }

  async function loadPaidInFull() {
    const res = await fetch(`/api/reports/judgments/paid-in-full`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to load Paid in Full (HTTP ${res.status})`);
    return res.json();
  }

  async function refresh() {
    setLoading(true);
    setError(null);

    try {
      if (module === "job_profitability") {
        const data = await loadJobProfitability();
        setProfitability(data);
        setAr(null);
        setTop(null);
        setPaid(null);
      } else if (module === "process_ar") {
        const [arData, topData] = await Promise.all([loadAR(), loadTopClients()]);
        setAr(arData);
        setTop(topData);
        setPaid(null);
        setProfitability(null);
      } else if (module === "judgments") {
        const paidData = await loadPaidInFull();
        setPaid(paidData);
        setAr(null);
        setTop(null);
        setProfitability(null);
      }
    } catch (e) {
      console.error(e);
      setError(e.message || "Failed to load reports");
      setAr(null);
      setTop(null);
      setPaid(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, [module, days]);

  const overduePct = useMemo(() => {
    if (!ar) return 0;
    const out = Number(ar.outstanding || 0);
    const od = Number(ar.overdue_outstanding || 0);
    return out > 0 ? od / out : 0;
  }, [ar]);

  const overdueInvoices = useMemo(() => {
    const list = Array.isArray(ar?.invoices) ? ar.invoices : [];
    return list
      .map((inv) => ({
        ...inv,
        __balance: Number(inv.balance ?? inv.outstanding ?? inv.amount_due ?? 0) || 0,
        __daysPastDue: daysPastDue(inv.due_date),
      }))
      .filter((i) => i.__balance > 0 && i.__daysPastDue > 0)
      .sort((a, b) => (b.__daysPastDue || 0) - (a.__daysPastDue || 0))
      .slice(0, 10);
  }, [ar]);

  function Section({ title, right, children }) {
    return (
      <div style={ui.card}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 12,
            marginBottom: 14,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              letterSpacing: 0.3,
              color: "#dbeafe",
            }}
          >
            {title}
          </div>
          {right ? (
            <div style={{ fontSize: 12, color: "#9fb0c3" }}>{right}</div>
          ) : null}
        </div>
        {children}
      </div>
    );
  }

  
function Stat({ label, value, danger = false, hint = "" }) {
  return (
    <HeadquartersMetricCard
      label={label}
      value={value}
      subtext={hint}
      style={{
        border: danger
          ? "1px solid rgba(239,68,68,0.20)"
          : undefined,
      }}
    />
  );
}

function TopClientsTable({ rows }) {
    return (
      <div style={ui.subCard}>
        <div
          className="jt-report-table-top"
          style={{
            display: "grid",
            gap: 10,
            padding: "0 0 8px 0",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            fontSize: 12,
            color: "#9fb0c3",
            fontWeight: 700,
            marginBottom: 4,
          }}
        >
          <div>Client</div>
          <div>Outstanding</div>
          <div>Overdue</div>
          <div>Last Invoice</div>
        </div>

        {rows.length === 0 ? (
          <div style={{ padding: "12px 0", fontSize: 13, color: "#9fb0c3" }}>
            No data yet.
          </div>
        ) : (
          rows.map((r) => (
            <div
              key={r.client_id}
              className="jt-report-table-top"
              onClick={() => navigate(`/clients/${r.client_id}`)}
              title="Click to open Client Detail"
              style={{
                display: "grid",
                gap: 10,
                padding: "10px 0",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
                cursor: "pointer",
                fontSize: 13,
                alignItems: "center",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.03)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              <div style={{ fontWeight: 600, color: "#ffffff" }}>
                {r.client_name || `Client #${r.client_id}`}
              </div>
              <div style={{ color: "#ffffff" }}>{money(r.outstanding)}</div>
              <div style={{ color: "#f87171" }}>{money(r.overdue)}</div>
              <div style={{ color: "#9fb0c3" }}>{r.last_invoice_date || "—"}</div>
            </div>
          ))
        )}
      </div>
    );
  }

  function OverdueInvoicesTable({ rows }) {
    return (
      <div style={ui.subCard}>
        <div
          className="jt-report-table-4"
          style={{
            display: "grid",
            gap: 10,
            padding: "0 0 8px 0",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            fontSize: 12,
            color: "#9fb0c3",
            fontWeight: 700,
            marginBottom: 4,
          }}
        >
          <div>Invoice</div>
          <div>Client</div>
          <div>Balance</div>
          <div>Past Due</div>
        </div>

        {rows.length === 0 ? (
          <div style={{ padding: "12px 0", fontSize: 13, color: "#9fb0c3" }}>
            No overdue invoices.
          </div>
        ) : (
          rows.map((inv) => (
            <div
              key={inv.id}
              className="jt-report-table-4"
              onClick={() => {
                const invoiceId = inv.invoice_id ?? inv.id;
                if (!invoiceId) return;
                navigate(`/invoices/${invoiceId}`);
              }}
              title="Click to open Invoice Detail"
              style={{
                display: "grid",
                gap: 10,
                padding: "10px 0",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
                cursor: "pointer",
                fontSize: 13,
                alignItems: "center",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.03)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              <div style={{ fontWeight: 700, color: "#ffffff" }}>
                {inv.invoice_number || `#${inv.id}`}
              </div>
              <div style={{ fontWeight: 600, color: "#ffffff" }}>
                {inv.client_name || `Client #${inv.client_id || "—"}`}
              </div>
              <div style={{ color: "#ffffff" }}>{money(inv.__balance)}</div>
              <div style={{ color: "#f87171", fontWeight: 700 }}>
                {inv.__daysPastDue}d
              </div>
            </div>
          ))
        )}
      </div>
    );
  }

  function JudgmentsTable({ rows }) {
    return (
      <div style={ui.subCard}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr",
            gap: 10,
            padding: "0 0 8px 0",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            fontSize: 12,
            color: "#9fb0c3",
            fontWeight: 700,
            marginBottom: 4,
          }}
        >
          <div>Case #</div>
          <div>Principal</div>
          <div>Last Payment</div>
          <div>Balance Due</div>
        </div>

        {Array.isArray(rows) && rows.length > 0 ? (
          rows.map((r) => (
            <div
              key={r.judgment_id}
              onClick={() => navigate(`/judgments/${r.judgment_id}`)}
              title="Click to open Judgment"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: 10,
                padding: "10px 0",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
                cursor: "pointer",
                fontSize: 13,
                alignItems: "center",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.03)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              <div style={{ fontWeight: 700, color: "#ffffff" }}>
                {r.case_number || `#${r.judgment_id}`}
              </div>
              <div style={{ color: "#ffffff" }}>{money(r.principal)}</div>
              <div style={{ color: "#9fb0c3" }}>{r.last_payment_date || "—"}</div>
              <div style={{ color: "#ffffff", fontWeight: 700 }}>
                {r.current_balance_due != null ? money(r.current_balance_due) : "—"}
              </div>
            </div>
          ))
        ) : (
          <div style={{ padding: "12px 0", fontSize: 13, color: "#9fb0c3" }}>
            No paid-in-full judgments yet.
          </div>
        )}
      </div>
    );
  }

  return (
    <HeadquartersPage
      title="Reports"
      subtitle="Financial reporting built for operators and decision makers."
    >
      <HeadquartersSection>
        <div
          className="jt-reports-toolbar"
        >
          <select
            value={module}
            onChange={(e) => setModule(e.target.value)}
            style={ui.input}
          >
            <option value="job_profitability">Job Profitability & Cost Analysis</option>
            <option value="books">Checklist and books</option>
            <option value="process_ar">Process Serving</option>
            <option value="judgments">Judgments</option>
          </select>

          {module !== "books" && (
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            style={ui.input}
          >
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last 12 months</option>
          </select>
          )}

          {module !== "books" && (
          <button type="button" onClick={refresh} disabled={loading} style={ui.btnPrimary}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          )}
        </div>
      </HeadquartersSection>

      {error ? (
        <div
          style={{
            ...ui.card,
            color: "#fecaca",
            border: "1px solid rgba(239,68,68,0.35)",
            background: "rgba(127,29,29,0.25)",
          }}
        >
          {error}
        </div>
      ) : null}


      {module === "books" && <Books embedded />}

      {module === "job_profitability" && (
        <div style={{ marginTop: 20 }}>
          <Section
            title="Job Profitability & Cost Analysis"
            right={profitability?.range ? `${profitability.range.start_date} → ${profitability.range.end_date}` : rangeLabel}
          >
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <button
                type="button"
                onClick={exportProfitabilityCsv}
                disabled={!profitability || !(profitability.rows || []).length}
                style={ui.btnSecondary}
              >
                Export CSV
              </button>
            </div>

            <div className="jt-reports-stats-grid">
              <Stat
                label="Revenue"
                value={profitability ? money(profitability.summary?.revenue) : "—"}
              />
              <Stat
                label="Costs"
                value={profitability ? money(profitability.summary?.costs) : "—"}
              />
              <Stat
                label="Profit"
                value={profitability ? money(profitability.summary?.profit) : "—"}
              />
              <Stat
                label="Margin"
                value={
                  profitability?.summary?.margin != null
                    ? `${Number(profitability.summary.margin).toFixed(2)}%`
                    : "—"
                }
              />
            </div>

            <div style={{ marginTop: 16 }} className="jt-reports-stats-grid">
              <Stat
                label="Estimated Costs"
                value={profitability ? money(profitability.summary?.estimated_costs) : "—"}
              />
              <Stat
                label="Actual Costs"
                value={profitability ? money(profitability.summary?.actual_costs) : "—"}
              />
              <Stat
                label="Variance"
                value={profitability ? money(profitability.summary?.variance) : "—"}
              />
            </div>

            <div
              style={{
                fontWeight: 600,
                marginTop: 20,
                marginBottom: 10,
                fontSize: 14,
                letterSpacing: 0.3,
                color: "#dbeafe",
              }}
            >
              Cost Breakdown
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
                gap: 10,
                marginBottom: 18,
              }}
            >
              {(profitability?.category_breakdown || []).length === 0 ? (
                <div style={{ color: "#9fb0c3" }}>No job cost categories yet.</div>
              ) : (
                profitability.category_breakdown.map((row) => (
                  <div
                    key={row.category}
                    style={{
                      ...ui.subCard,
                      padding: 14,
                    }}
                  >
                    <div style={{ color: "#9fb0c3", fontSize: 12, marginBottom: 4 }}>
                      {row.category}
                    </div>
                    <div style={{ color: "#ffffff", fontSize: 18, fontWeight: 800 }}>
                      {money(row.total)}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div
              style={{
                fontWeight: 600,
                marginBottom: 10,
                fontSize: 14,
                letterSpacing: 0.3,
                color: "#dbeafe",
              }}
            >
              Jobs
            </div>

            <div style={ui.subCard}>
              <div
                className="jt-report-table-profitability jt-report-profitability-header"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.1fr 1.3fr 0.8fr 0.8fr 0.8fr 0.7fr 0.8fr",
                  gap: 10,
                  padding: "0 0 8px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                  fontSize: 12,
                  color: "#9fb0c3",
                  fontWeight: 700,
                  marginBottom: 4,
                }}
              >
                <div>Job</div>
                <div>Client</div>
                <div>Revenue</div>
                <div>Costs</div>
                <div>Profit</div>
                <div>Margin</div>
                <div>Status</div>
              </div>

              {(profitability?.rows || []).length === 0 ? (
                <div style={{ padding: "12px 0", fontSize: 13, color: "#9fb0c3" }}>
                  No profitability data yet. Add invoices and job costs to see this report.
                </div>
              ) : (
                profitability.rows.map((r) => (
                  <div
                    key={r.job_id}
                    className="jt-report-table-profitability jt-report-profitability-row"
                    onClick={() => navigate(`/jobs/${r.job_id}`)}
                    title="Click to open Job Detail"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1.1fr 1.3fr 0.8fr 0.8fr 0.8fr 0.7fr 0.8fr",
                      gap: 10,
                      padding: "10px 0",
                      borderBottom: "1px solid rgba(255,255,255,0.05)",
                      cursor: "pointer",
                      fontSize: 13,
                      alignItems: "center",
                      transition: "all 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "#ffffff" }} data-label="Job">
                      {r.job_number || `JOB #${r.job_id}`}
                    </div>
                    <div
                      data-label="Client"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (r.client_id) navigate(`/clients/${r.client_id}`);
                      }}
                      title="Click to open Client"
                      style={{
                        color: "#00c2ff",
                        fontWeight: 700,
                        cursor: r.client_id ? "pointer" : "default",
                      }}
                    >
                      {r.client_name || "—"}
                    </div>
                    <div style={{ color: "#ffffff" }} data-label="Revenue">{money(r.revenue)}</div>
                    <div style={{ color: "#f5c542" }} data-label="Costs">{money(r.costs)}</div>
                    <div
                      data-label="Profit"
                      style={{ color: Number(r.profit || 0) >= 0 ? "#86efac" : "#f87171", fontWeight: 700 }}
                    >
                      {money(r.profit)}
                    </div>
                    <div style={{ color: "#67e8f9" }} data-label="Margin">
                      {Number(r.margin || 0).toFixed(2)}%
                    </div>
                    <div data-label="Status">
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 9px",
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 900,
                          background:
                            r.status === "PAID"
                              ? "rgba(34,197,94,0.16)"
                              : "rgba(245,197,66,0.14)",
                          color: r.status === "PAID" ? "#86efac" : "#f5c542",
                        }}
                      >
                        {r.status || "—"}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Section>
        </div>
      )}

      {module === "process_ar" && (
        <div style={{ marginTop: 20 }}>
          <Section
            title="Accounts Receivable Overview"
            right={ar?.range ? `${ar.range.start_date} → ${ar.range.end_date}` : rangeLabel}
          >
            <div
              className="jt-reports-stats-grid"
            >
              <Stat label="Total Invoiced" value={ar ? money(ar.total_invoiced) : "—"} />
              <Stat label="Total Paid" value={ar ? money(ar.total_paid) : "—"} />
              <Stat label="Outstanding" value={ar ? money(ar.outstanding) : "—"} />
              <Stat
                label="Overdue"
                value={ar ? money(ar.overdue_outstanding) : "—"}
                danger
                hint="High priority"
              />
            </div>

            <div
              style={{
                fontWeight: 600,
                marginBottom: 10,
                fontSize: 14,
                letterSpacing: 0.3,
                color: "#dbeafe",
              }}
            >
              Overdue Invoices
            </div>

            <OverdueInvoicesTable rows={overdueInvoices} />

            <div
              style={{
                marginTop: 14,
                fontSize: 12,
                color: "#facc15",
                fontWeight: 600,
              }}
            >
              ⚠ Overdue Rate: {pct(overduePct)}
            </div>
          </Section>

          <Section title="Top Clients" right={rangeLabel}>
            <TopClientsTable rows={Array.isArray(top?.rows) ? top.rows : []} />
          </Section>
        </div>
      )}

      {module === "judgments" && (
        <div style={{ marginTop: 20 }}>
          <Section title="Paid in Full Judgments">
            <JudgmentsTable rows={Array.isArray(paid?.rows) ? paid.rows : []} />
          </Section>
        </div>
      )}
    </HeadquartersPage>
  );
}

function Section({ title, right, children }) {
  return (
    <div style={{ ...ui.card, marginTop: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 12,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: 14,
            letterSpacing: 0.3,
            color: "#dbeafe",
          }}
        >
          {title}
        </div>
        {right ? (
          <div style={{ fontSize: 12, color: "#9fb0c3" }}>{right}</div>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, danger = false, hint = "" }) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 14,
        padding: 16,
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: 12, color: "#9fb0c3", marginBottom: 6 }}>{label}</div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: danger ? "#f87171" : "#ffffff",
        }}
      >
        {value}
      </div>
      {hint ? (
        <div style={{ marginTop: 6, fontSize: 12, color: "#9fb0c3" }}>{hint}</div>
      ) : null}
    </div>
  );
}
